/**
 * Admin Dashboard
 * Overview of platform stats, users, and content
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components';
import api from '../../services/api';
import { formatRelativeTime } from '../../utils/dateUtils';
import SEO from '../../components/common/SEO';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCourses: 0,
    activeTeachers: 0,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchRecentUsers();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await api.getAdminStats();
      setStats({
        totalUsers: (data.users?.totalStudents || 0) + (data.users?.totalTeachers || 0),
        totalCourses: data.courses?.total || 0,
        activeTeachers: data.users?.totalTeachers || 0,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentUsers = async () => {
    try {
      const data = await api.getUsers({ page: 1, limit: 4 });
      if (data.users) {
        const formattedUsers = data.users.map(user => ({
          id: user.id || user._id,
          name: user.name,
          email: user.email,
          role: user.role,
          avatar: user.avatar,
          createdAt: user.createdAt,
        }));
        setRecentUsers(formattedUsers);
      }
    } catch (error) {
      console.error('Error fetching recent users:', error);
    }
  };

  const platformStats = [
    {
      label: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      icon: (
        <svg className="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      bgColor: 'bg-primary-100',
    },
    {
      label: 'Total Courses',
      value: stats.totalCourses.toLocaleString(),
      icon: (
        <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      bgColor: 'bg-green-100',
    },
    {
      label: 'Active Teachers',
      value: stats.activeTeachers.toLocaleString(),
      icon: (
        <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      ),
      bgColor: 'bg-purple-100',
    },
  ];

  return (
    <AdminLayout>
      <SEO title="Admin Dashboard" noIndex />
      {/* Header */}
      <div className="lh-admin-dashboard__header mb-8">
        <h1 className="lh-admin-dashboard__title text-2xl md:text-3xl font-bold text-dark-900 mb-1">
          Admin Dashboard ⚙️
        </h1>
        <p className="lh-admin-dashboard__subtitle text-dark-500">
          Monitor and manage your learning platform
        </p>
      </div>

      {/* Stats Grid */}
      <div className="lh-admin-dashboard__stats grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {loading ? (
          platformStats.map((_, index) => (
            <div key={index} className="card p-5 animate-pulse">
              <div className="h-12 bg-dark-200 rounded-lg mb-3"></div>
              <div className="h-8 bg-dark-200 rounded mb-2"></div>
              <div className="h-4 bg-dark-200 rounded w-2/3"></div>
            </div>
          ))
        ) : (
          platformStats.map((stat, index) => (
            <div key={index} className={`lh-admin-dashboard__stat card p-5`}>
              <div className={`lh-admin-dashboard__stat-icon w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center mb-3`}>
                {stat.icon}
              </div>
              <p className="lh-admin-dashboard__stat-value text-2xl font-bold text-dark-900">{stat.value}</p>
              <p className="lh-admin-dashboard__stat-label text-sm text-dark-500">{stat.label}</p>
            </div>
          ))
        )}
      </div>

      {/* Main Content Grid */}
      <div className="lh-admin-dashboard__content grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Users */}
        <div className="lh-admin-dashboard__users">
          <div className="lh-admin-dashboard__users-card card">
            <div className="lh-admin-dashboard__users-header flex items-center justify-between p-5 border-b border-dark-100">
              <h2 className="lh-admin-dashboard__users-title text-lg font-bold text-dark-900">Recent Users</h2>
              <Link to="/admin/users" className="lh-admin-dashboard__users-link text-primary-500 hover:text-primary-600 text-sm font-medium">
                View All →
              </Link>
            </div>
            <div className="lh-admin-dashboard__users-list divide-y divide-dark-100">
              {recentUsers.length > 0 ? (
                recentUsers.map((user) => (
                  <div key={user.id} className="lh-admin-dashboard__user-item flex items-center justify-between p-5">
                    <div className="lh-admin-dashboard__user-info flex items-center gap-3">
                      <div className="lh-admin-dashboard__user-avatar w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span>{user.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                        )}
                      </div>
                      <div className="lh-admin-dashboard__user-details">
                        <p className="lh-admin-dashboard__user-name font-medium text-dark-900">{user.name}</p>
                        <p className="lh-admin-dashboard__user-email text-sm text-dark-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="lh-admin-dashboard__user-meta flex items-center gap-4">
                      <span className={`lh-admin-dashboard__user-role text-xs font-medium px-2.5 py-1 rounded-full ${
                        user.role === 'teacher'
                          ? 'bg-primary-100 text-primary-600'
                          : user.role === 'admin'
                          ? 'bg-red-100 text-red-600'
                          : 'bg-dark-100 text-dark-600'
                      }`}>
                        {user.role}
                      </span>
                      <span className="lh-admin-dashboard__user-date text-sm text-dark-400">{formatRelativeTime(user.createdAt)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-5 text-center text-dark-500">No users yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="lh-admin-dashboard__quick-links">
          <div className="lh-admin-dashboard__quick-links-card card p-5">
            <h3 className="lh-admin-dashboard__quick-links-title text-lg font-bold text-dark-900 mb-4">Quick Links</h3>
            <div className="lh-admin-dashboard__quick-links-list space-y-3">
              <Link
                to="/admin/users"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-dark-50 transition-colors border border-dark-100"
              >
                <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm text-dark-700">Manage Users</span>
              </Link>
              <Link
                to="/admin/courses"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-dark-50 transition-colors border border-dark-100"
              >
                <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="text-sm text-dark-700">Manage Courses</span>
              </Link>
              <Link
                to="/admin/courses/create"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-dark-50 transition-colors border border-dark-100"
              >
                <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="text-sm text-dark-700">Create Course</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
